import { useMemo, useState } from 'react'
import {
  formatTime, nameIn, type BoardRoom, type HousekeepingState, type OccupancyState,
  type ServiceState,
} from '@hk/core'
import {
  Identifier, Table, TBody, TD, TH, THead, TableWrap, useLocale,
} from '@hk/ui'
import { ArrowDown, ArrowUp } from 'lucide-react'
import { STRINGS } from '../i18n/strings'

/* ============================================================================
   W-22 — THE ROOM LIST.  Sprint 63.

   **IT IS A SECOND VIEW OF W-20 AND NOT A SECOND SCREEN**, and that is a divergence from
   the pack (Change Note 08). The packs draw the board and the list as two entries; they
   are the same five hundred rooms behind the same search and the same filter chips, and a
   sidebar offering both would make a supervisor choose a screen before they have chosen a
   question. The toggle sits above the rooms, where the choice actually is.

   ── WHAT A LIST ANSWERS THAT A GRID CANNOT ─────────────────────────────────────
   The board is a picture: three dimensions in colour, read at a glance, and unbeatable
   for "how does the hotel look this morning". It is poor at "every room the guest has not
   left yet, in floor order, with who is on it" — that is a list of facts, and a tile has
   room for a number and a colour.

   **SO THE COLUMNS ARE THE THINGS THE TILE HAD TO LEAVE OUT**: the three states in words
   rather than in colour, who is holding the room, and when the guest goes. Sorting is the
   whole point of the view and it is stable — see `sorted`.
   ========================================================================= */

type SortKey = 'room' | 'housekeeping' | 'occupancy' | 'service' | 'assigned' | 'departure'

/** **THE ORDER STATES SORT IN IS THE ORDER WORK HAPPENS IN**, not the alphabet. A
 *  supervisor sorting by condition wants the rooms needing most work first; "Clean,
 *  Dirty, In progress, Inspected" is what an alphabetical sort would give them. */
const HK_ORDER: Record<HousekeepingState, number> = {
  DIRTY: 0, IN_PROGRESS: 1, CLEAN: 2, INSPECTED: 3,
}
const OCC_ORDER: Record<OccupancyState, number> = {
  OCCUPIED: 0, DEPARTING: 1, ARRIVING: 2, VACANT: 3,
}
const SVC_ORDER: Record<ServiceState, number> = {
  OUT_OF_ORDER: 0, OUT_OF_SERVICE: 1, IN_SERVICE: 2,
}

export function RoomList({
  rooms, floorLabel, onOpen,
}: {
  rooms: BoardRoom[]
  /** The board already knows how to name a floor; the list does not learn it again. */
  floorLabel: (floorId: string) => string
  onOpen: (room: BoardRoom) => void
}) {
  const { locale } = useLocale()
  const t = STRINGS[locale]

  const [key, setKey] = useState<SortKey>('room')
  const [descending, setDescending] = useState(false)

  const sorted = useMemo(() => {
    /* **A COPY, AND A STABLE SORT.** `Array.prototype.sort` mutates, and the array here is
       the board's own filtered list — sorting it in place would reorder the grid behind
       the toggle. And rooms that tie on the sort key fall back to the ROOM NUMBER rather
       than to whatever order the server happened to send, so a supervisor who sorts by
       condition twice sees the same list twice. */
    const rank = (r: BoardRoom): number | string => {
      switch (key) {
        case 'housekeeping': return HK_ORDER[r.housekeeping]
        case 'occupancy': return OCC_ORDER[r.occupancy]
        case 'service': return SVC_ORDER[r.service]
        case 'assigned': return r.assignedToName ?? '￿'
        case 'departure': return r.departureUtc ?? '￿'
        default: return ''
      }
    }

    /* Room numbers are IDENTIFIERS and compare as text — "10" before "9" is wrong, and
       `numeric` is what puts them in the order they are painted on the doors. */
    const byNumber = (a: BoardRoom, b: BoardRoom) =>
      a.roomNumber.localeCompare(b.roomNumber, 'en', { numeric: true })

    return [...rooms].sort((a, b) => {
      if (key !== 'room') {
        const ra = rank(a)
        const rb = rank(b)
        if (ra < rb) return descending ? 1 : -1
        if (ra > rb) return descending ? -1 : 1
        return byNumber(a, b)
      }
      return descending ? byNumber(b, a) : byNumber(a, b)
    })
  }, [rooms, key, descending])

  const press = (next: SortKey) => {
    if (next === key) { setDescending((d) => !d); return }
    setKey(next)
    setDescending(false)
  }

  const Head = ({ column, label }: { column: SortKey; label: string }) => (
    <TH>
      <button
        type="button"
        onClick={() => press(column)}
        /* **THE WHOLE HEADING IS THE CONTROL.** A sort arrow small enough to be decorative
           is a control nobody finds; the word is what people aim at. */
        className="inline-flex items-center gap-1 font-inherit hover:text-foreground cursor-pointer"
        aria-label={`${label} — ${t.rlSortBy}`}
      >
        {label}
        {key === column && (descending
          ? <ArrowDown className="size-3" aria-hidden />
          : <ArrowUp className="size-3" aria-hidden />)}
      </button>
    </TH>
  )

  return (
    <TableWrap>
      <Table>
        <THead>
          <tr>
            <Head column="room" label={t.rlRoom} />
            <TH>{t.rlType}</TH>
            <TH>{t.rlFloor}</TH>
            <Head column="housekeeping" label={t.rlCondition} />
            <Head column="occupancy" label={t.rlOccupancy} />
            <Head column="service" label={t.rlService} />
            <Head column="assigned" label={t.rlAssigned} />
            <Head column="departure" label={t.rlDeparture} />
          </tr>
        </THead>
        <TBody>
          {sorted.map((room) => (
            <tr
              key={room.roomId}
              onClick={() => onOpen(room)}
              className="cursor-pointer hover:bg-muted/50"
            >
              <TD>
                {/* The row opens W-21. A button rather than the row alone, so a keyboard
                    reaches it and a screen reader is told what it is. */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onOpen(room) }}
                  className="cursor-pointer font-semibold text-primary hover:underline"
                >
                  <Identifier>{room.roomNumber}</Identifier>
                </button>
              </TD>
              <TD>{nameIn(locale, room.roomType, room.roomTypeAr)}</TD>
              <TD><Identifier>{floorLabel(room.floorId)}</Identifier></TD>
              {/* **THE THREE DIMENSIONS IN WORDS.** On the board they are colour, a border
                  and a corner chip; a list that repeated the colours and nothing else
                  would be a worse board rather than a list. */}
              <TD>{t.tones[TONE[room.housekeeping]]}</TD>
              <TD>{t.rlOcc[room.occupancy]}</TD>
              <TD>{room.service === 'IN_SERVICE' ? '—' : t.rlSvc[room.service]}</TD>
              {/* **NOBODY IS A DASH, NOT A BLANK.** An empty cell reads as a column that
                  failed to load; a dash is the product saying it looked. */}
              <TD>
                {room.assignedToName
                  ? <bdi>{room.assignedToName}</bdi>
                  : <span className="text-muted-fg">—</span>}
              </TD>
              <TD>
                {room.departureUtc
                  ? formatTime(new Date(room.departureUtc), locale)
                  : <span className="text-muted-fg">—</span>}
              </TD>
            </tr>
          ))}
        </TBody>
      </Table>
    </TableWrap>
  )
}

/** The board's four visual tones, so one word names one state across both views. */
const TONE = {
  DIRTY: 'dirty', IN_PROGRESS: 'prog', CLEAN: 'clean', INSPECTED: 'ready',
} as const
